import React, { useState } from 'react';
import { FiExternalLink } from 'react-icons/fi';
import { useTask } from '../contexts/TaskContext';
import { toast } from 'react-toastify';
import '../styles/SplitSubtaskButton.css';

/**
 * Button to split a subtask into its own task card.
 * 
 * @param {Object} props
 * @param {Object} props.parentTask - The parent task containing the subtask
 * @param {number} props.subtaskIndex - Index of the subtask to split
 * @param {Object} props.subtask - The subtask object { title, completed }
 * @param {boolean} props.removeAfterSplit - Whether to remove subtask from parent after splitting
 * @param {Function} props.onSplit - Optional callback after successful split
 */
const SplitSubtaskButton = ({ 
  parentTask, 
  subtaskIndex, 
  subtask, 
  removeAfterSplit = true,
  onSplit 
}) => {
  const { createTask, updateTask } = useTask();
  const [isSplitting, setIsSplitting] = useState(false);

  const handleSplit = async (e) => {
    e.stopPropagation();
    
    if (isSplitting) return;
    
    const subtaskTitle = typeof subtask === 'string' ? subtask : subtask.title;
    
    if (!subtaskTitle?.trim()) {
      toast.error('Cannot split empty subtask');
      return;
    }

    setIsSplitting(true);

    try {
      // Create new task from subtask
      const newTaskData = {
        title: subtaskTitle,
        description: `Split from task: "${parentTask.title}"`,
        urgency: parentTask.urgency || 3,
        status: subtask.completed ? 'done' : 'todo',
        deadline: parentTask.deadline || '',
        assignees: parentTask.assignees?.map(u => u._id || u) || [],
      };

      const result = await createTask(newTaskData);

      if (result.success) {
        // Optionally remove subtask from parent
        if (removeAfterSplit) {
          const updatedSubtasks = parentTask.subtasks.filter((_, i) => i !== subtaskIndex);
          await updateTask(parentTask._id, { subtasks: updatedSubtasks });
        }

        toast.success(`Subtask "${subtaskTitle}" converted to task`);
        
        if (onSplit) {
          onSplit(result.task);
        }
      }
    } catch (err) {
      console.error('Failed to split subtask:', err);
      toast.error('Failed to split subtask');
    } finally {
      setIsSplitting(false);
    }
  };

  return (
    <button
      className="split-subtask-btn"
      onClick={handleSplit}
      disabled={isSplitting}
      title="Convert to separate task"
    >
      <FiExternalLink size={14} />
    </button>
  );
};

export default SplitSubtaskButton;
